import { Skeleton, Stack } from "@mui/material";
import { GoogleMap } from "@react-google-maps/api";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useGoogleMapsSdkContext } from "@src/appV2/lib/GoogleMaps/context";

import { IconButton } from "../../../components/IconButton";
import { GoogleMapCustomMarkerWrapper } from "../../../GeoLocation/GoogleMapCustomMarkerWrapper";
import { GoogleMapErrorBoundary } from "../../../GeoLocation/GoogleMapErrorBoundary";
import { MapMarkerLocation } from "../../../GeoLocation/MapMarkerLocation";
import { type Facility } from "../../data/schemas";
import { FacilityMarker } from "./FacilityMarker";

interface FacilityMapProps {
  facilities: Facility[];
  onFacilitySelect: (facility: Facility) => void;
  workerLocation: google.maps.LatLngLiteral | undefined;
  mapState: [google.maps.Map | undefined, (map: google.maps.Map | undefined) => void];
}

export const DEFAULT_MAP_ZOOM = 10;
const googleMapOptions: google.maps.MapOptions = {
  disableDefaultUI: true,
  clickableIcons: false,
  gestureHandling: "greedy",
  minZoom: 4,
  maxZoom: 16,
  styles: [
    {
      elementType: "labels.icon",
      stylers: [{ visibility: "off" }],
    },
  ],
};

export function FacilityMap(props: FacilityMapProps) {
  const { facilities, onFacilitySelect, workerLocation, mapState } = props;
  const [map, setMap] = mapState;
  const { isSdkLoaded, sdkLoadError } = useGoogleMapsSdkContext();

  if (!isSdkLoaded) {
    return (
      <Stack sx={{ flex: 1, width: "100%", height: "100%" }}>
        <Skeleton variant="rectangular" width="100%" height="100%" />
      </Stack>
    );
  }

  if (sdkLoadError) {
    return (
      <Stack sx={{ flex: 1, alignItems: "center", justifyContent: "center", padding: 4 }}>
        Failed to load map. Please refresh the page.
      </Stack>
    );
  }

  return (
    <GoogleMapErrorBoundary>
      <Stack sx={{ position: "relative", flex: 1, width: "100%", height: "100%" }}>
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%", minHeight: "400px" }}
          zoom={DEFAULT_MAP_ZOOM}
          options={googleMapOptions}
          onLoad={(loadedMap) => {
            setMap(loadedMap);
            if (workerLocation) {
              loadedMap.setCenter(workerLocation);
            }
          }}
          onUnmount={() => {
            setMap(undefined);
          }}
        >
          {map &&
            facilities.map((facility) => (
              <GoogleMapCustomMarkerWrapper
                key={facility.id}
                position={{ lat: facility.lat, lng: facility.lng }}
              >
                <FacilityMarker
                  onClick={() => {
                    logEvent(APP_V2_USER_EVENTS.EARLY_ACCESS_FACILITIES_MAP_PIN_CLICKED, {
                      facilityId: facility.id,
                      facilityName: facility.name,
                      city: facility.city,
                    });
                    onFacilitySelect(facility);
                  }}
                />
              </GoogleMapCustomMarkerWrapper>
            ))}

          {/* Worker location marker */}
          {workerLocation && (
            <GoogleMapCustomMarkerWrapper position={workerLocation}>
              <MapMarkerLocation />
            </GoogleMapCustomMarkerWrapper>
          )}
        </GoogleMap>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          sx={(theme) => ({
            position: "absolute",
            bottom: theme.spacing(4),
            right: theme.spacing(4),
            backgroundColor: theme.palette.background.tertiary,
            borderRadius: theme.borderRadius?.xLarge,
            boxShadow: theme.shadows[2],
          })}
        >
          <IconButton
            invert
            iconType="minus"
            variant="contained"
            onClick={() => {
              const currentZoom = map?.getZoom();
              if (map && currentZoom) {
                map.setZoom(currentZoom - 1);
              }
            }}
          />
          <IconButton
            invert
            iconType="plus"
            variant="contained"
            onClick={() => {
              const currentZoom = map?.getZoom();
              if (map && currentZoom) {
                map.setZoom(currentZoom + 1);
              }
            }}
          />
        </Stack>
      </Stack>
    </GoogleMapErrorBoundary>
  );
}
